import { BoardConnection, Source } from '../../../types';
import { ConnectionEditState } from './Connections.types';
import { parseConnectionConfiguration } from '../../../sources/source-settings/SourceSettingsConfiguration';

// The key was renamed when votes started being sent to the region. Until then the
// React app marked a source "Voted" in local storage without posting anything, so
// every flag stored under the old key may stand for a vote nobody received.
export const SOURCE_VOTES_KEY = 'connections.sourceVotes.v2';

export const sortConnections = (
  left: BoardConnection,
  right: BoardConnection,
): number => (left.name || '').localeCompare(right.name || '');

export const sortSources = (left: Source, right: Source): number => {
  if (left.backlog !== right.backlog) {
    return left.backlog ? 1 : -1;
  }

  return left.displayName.localeCompare(right.displayName);
};

export const parseStoredVotes = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

const toTime = (value?: Date | string | null): number | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

/**
 * Whether sync for a connection is currently suspended.
 *
 * Two conditions, both of which have to hold:
 *
 * 1. `disableUntil` is still in the future. This mirrors the server's own test in BoardSyncService
 *    and ScheduleEventService.
 * 2. No sync has succeeded since the window ended. A sync newer than `disableUntil` means the
 *    connection recovered, so the failure is history whatever the stored fields say.
 *
 * There is no `disabledAt`, so a sync that landed *inside* an unexpired window cannot be told from
 * one that predates it. That case does not arise in practice: BoardSyncService skips a disabled
 * connection outright, so it cannot sync while the window is open.
 *
 * `disabledReason` is never cleared when a connection recovers, so it is never read as a disabled
 * signal on its own.
 *
 * @param disableUntil The connection's `disableUntil` timestamp, if any.
 * @param lastSync When the connection last synced, if ever.
 * @param now Reference time; defaults to the current time. Injected by tests.
 * @returns `true` while sync is suspended, otherwise `false`.
 */
export const isConnectionSyncDisabled = (
  disableUntil?: Date | string | null,
  lastSync?: Date | string | null,
  now: Date = new Date(),
): boolean => {
  const until = toTime(disableUntil);

  if (until === null || until <= now.getTime()) {
    return false;
  }

  const synced = toTime(lastSync);

  return synced === null || synced <= until;
};

/**
 * The connection with its configuration replaced, in both the forms the API reads.
 *
 * @param connection The connection to update.
 * @param configuration The configuration it should now carry.
 * @returns A copy of the connection; the original is untouched.
 * @throws When the configuration cannot be serialised to JSON.
 */
export const withConfiguration = (
  connection: BoardConnection,
  configuration: unknown,
): BoardConnection => ({
  ...connection,
  configuration,
  configurationJson: JSON.stringify(configuration, null, 2),
});

/**
 * The connection with an edit applied to its plain fields: everything the edit form holds except
 * the configuration.
 *
 * @param connection The connection to update.
 * @param edit The edited fields; fields it leaves out keep the connection's values.
 * @returns A copy of the connection; the original is untouched.
 */
export const withEditedFields = (
  connection: BoardConnection,
  edit: Partial<ConnectionEditState>,
): BoardConnection => {
  const next: BoardConnection = { ...connection };

  if (edit.name !== undefined) next.name = edit.name.trim();
  if (edit.colour !== undefined) next.colour = edit.colour;
  if (edit.defaultStatusId !== undefined) next.defaultStatusId = edit.defaultStatusId || undefined;
  if (edit.archived !== undefined) next.archived = edit.archived;
  if (edit.manageTasksHere !== undefined) next.manageTasksHere = edit.manageTasksHere;
  if (edit.defaultToTop !== undefined) next.defaultToTop = edit.defaultToTop;
  if (edit.tagName !== undefined) next.tagName = edit.tagName.trim() || undefined;
  if (edit.newTaskDaysLimit !== undefined) {
    next.newTaskDaysLimit =
      edit.newTaskDaysLimit.trim() === '' ? undefined : Number(edit.newTaskDaysLimit);
  }

  return next;
};

export const toEditState = (connection: BoardConnection): ConnectionEditState => ({
  id: connection.id,
  sourceTypeId: connection.sourceTypeId,
  name: connection.name || '',
  colour: connection.colour || '#0078d4',
  defaultStatusId: connection.defaultStatusId || '',
  archived: connection.archived,
  manageTasksHere: connection.manageTasksHere,
  defaultToTop: connection.defaultToTop,
  tagName: connection.tagName || '',
  newTaskDaysLimit:
    connection.newTaskDaysLimit !== undefined && connection.newTaskDaysLimit !== null
      ? String(connection.newTaskDaysLimit)
      : '',
  profileName: connection.profileName || '',
  profileId: connection.profileId || '',
  isAuthorised: connection.isAuthorised,
  configuration: parseConnectionConfiguration(
    connection.configuration ?? connection.configurationJson ?? {},
  ),
  lastFullSync: connection.lastFullSync,
  lastSync: connection.lastSync,
  message: connection.message,
  disableUntil: connection.disableUntil,
  disabledReason: connection.disabledReason,
});
